import React from 'react';
import { Card, Typography, Modal, Badge, Tooltip, Spin, Grid, Button, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { LoadingOutlined } from '@ant-design/icons';
import {
    WhatsAppOutlined,
    FacebookOutlined,
    MessageOutlined,
    EditOutlined,
    DeleteOutlined,
    PlayCircleOutlined,
    PauseCircleOutlined,
    CommentOutlined,
} from '@ant-design/icons';
import { useTheme } from '../../Shared/GlobalContext/ThemeContext';
import { useNavigate } from 'react-router-dom';

const { Text } = Typography;
const { useBreakpoint } = Grid;

const getPlatformConfig = (platformType, t) => {
    switch (platformType?.toLowerCase()) {
        case 'whatsapp':
            return {
                icon: WhatsAppOutlined,
                color: '#25D366',
                name: t('whatsapp')
            };
        case 'messenger':
            return {
                icon: MessageOutlined,
                color: '#1877F2',
                name: t('messenger')
            };
        case 'facebook':
            return {
                icon: FacebookOutlined,
                color: '#1877F2',
                name: t('facebook')
            };
        default:
            return {
                icon: FacebookOutlined,
                color: '#1877F2',
                name: t('facebook')
            };
    }
};

const AgentCard = ({ agent, onStatusChange, onSettings, onDelete }) => {
    const { t } = useTranslation();
    const screens = useBreakpoint();
    const { isDarkMode, toggleDarkMode } = useTheme();
    const isMobile = screens.xs;
    const [loading, setLoading] = React.useState(false);
    const isRunning = agent.status === 'started';
    const platform = getPlatformConfig(agent.platform_account?.platform_type, t);
    const PlatformIcon = platform.icon;
    const navigate = useNavigate();

    const themeColors = {
        background: isDarkMode ? '#141414' : '#fff',
        text: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.88)',
        secondaryText: isDarkMode ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)',
        cardHover: isDarkMode ? '#1f1f1f' : '#fafafa',
        divider: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.06)'
    };

    const handleDelete = () => {
        Modal.confirm({
            title: t('delete_agent'),
            content: t('are_you_sure_you_want_to_delete_agent', { name: agent.name }),
            okText: t('delete'),
            okType: 'danger',
            cancelText: t('cancel'),
            onOk: () => onDelete(agent.id)
        });
    };

    const localLoading = (state) => {
        setLoading(state);
    }
    const handleStatusToggle = () => {
        const updatedAgent = {
            status: isRunning ? 'stopped' : 'started',
        };
        onStatusChange(updatedAgent, agent.id, localLoading);
    };

    const handleTestAgent = () => {
        const platformType = agent.platform_account?.platform_type?.toLowerCase();
        if (platformType === 'facebook_page') {
            const pageId = agent.platform_account?.facebook_page_id;
            if (pageId) {
                window.open(`https://m.me/${pageId}`, '_blank');
            } else {
                message.warning(t('page_id_not_found'));
            }
        } else if (platformType === 'whatsapp') {
            message.warning(t('whatsapp_testing_not_available'));
        } else {
            message.warning(t('platform_testing_not_available'));
        }
    };

    const cardActions = [
        isRunning ? (
            <Tooltip title={t('pause')}>
                <Button
                    type="text"
                    icon={loading ? <Spin indicator={<LoadingOutlined spin />} size="small" /> : <PauseCircleOutlined style={{ color: isDarkMode ? '#d8b732' : '#c7c709' }} />}
                    onClick={handleStatusToggle}
                />
            </Tooltip>
        ) : (
            <Tooltip title={t('play')}>
                <Button
                    type="text"
                    icon={loading ? <Spin indicator={<LoadingOutlined spin />} size="small" /> : <PlayCircleOutlined style={{ color: isDarkMode ? '#52c41a' : '#00C853' }} />}
                    onClick={handleStatusToggle}
                />
            </Tooltip>
        ),
        <Tooltip title={t('test_agent')}>
            <Button
                type="text"
                icon={<CommentOutlined style={{ color: isDarkMode ? '#722ed1' : '#531dab' }} />}
                onClick={handleTestAgent}
            />
        </Tooltip>,
        <Tooltip title={t('edit')}>
            <Button
                type="text"
                icon={<EditOutlined style={{ color: isDarkMode ? '#177ddc' : '#1890ff' }} />}
                onClick={() => onSettings(agent)}
            />
        </Tooltip>,
        <Tooltip title={t('delete')}>
            <Button
                type="text"
                icon={<DeleteOutlined style={{ color: isDarkMode ? '#d32029' : '#ff4d4f' }} />}
                onClick={handleDelete}
            />
        </Tooltip>,
    ];

    return (
        <Card
            style={{
                display: 'flex',
                flexDirection: 'column',
                width: isMobile ? '100%' : '260px',
                background: themeColors.background,
                border: `1px solid ${themeColors.divider}`,
            }}
            bordered={false}
            styles={{
                body: {
                    padding: '20px',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}
            actions={cardActions}
        >
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px'
            }}>
                {agent.platform_account?.picture_url ? (
                    <img
                        src={agent.platform_account.picture_url}
                        alt={agent.platform_account.page_name}
                        style={{
                            width: '44px',
                            height: '44px',
                            borderRadius: '8px',
                            objectFit: 'cover'
                        }}
                    />
                ) : (
                    <div style={{
                        width: '44px',
                        height: '44px',
                        borderRadius: '8px',
                        background: platform.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <PlatformIcon style={{ fontSize: '26px', color: 'white' }} />
                    </div>
                )}
                <Badge
                    status={isRunning ? "success" : "error"}
                    text={
                        <Text style={{
                            fontSize: '14px',
                            padding: '4px 12px',
                            borderRadius: '4px',
                            background: isRunning ?
                                (isDarkMode ? '#162312' : '#f6ffed') :
                                (isDarkMode ? '#2a1215' : '#fff2f0'),
                            color: isRunning ?
                                (isDarkMode ? '#49aa19' : '#52c41a') :
                                (isDarkMode ? '#d32029' : '#ff4d4f')
                        }}>
                            {t(agent.status)}
                        </Text>
                    }
                />
            </div>

            <div style={{ flex: 1, overflow: 'hidden' }}>
                <div style={{
                    background: isDarkMode ? '#1d1d1d' : '#f0f2f5',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    marginBottom: '8px',
                    display: 'inline-block',
                    maxWidth: '100%'
                }}>
                    <Text type="secondary" style={{ fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center' }}>
                        <PlatformIcon style={{ marginInlineEnd: '4px', flexShrink: 0 }} />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{platform.name} {t('page_agent')}</span>
                    </Text>
                </div>
                <Text
                    strong
                    style={{
                        fontSize: '18px',
                        display: 'block',
                        marginBottom: '4px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        color: themeColors.text
                    }}
                    title={agent.name}
                >
                    {agent.name}
                </Text>
                <Text
                    style={{
                        fontSize: '14px',
                        display: 'block',
                        marginBottom: '4px',
                        color: isDarkMode ? '#177ddc' : '#1877F2',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }}
                    title={`${t('page')}: ${agent.platform_account?.page_name || t('no_page_name')}`}
                >
                    {t('page')}: {agent.platform_account?.page_name || t('no_page_name')}
                </Text>
                {agent.platform_account?.category && (
                    <Text
                        type="secondary"
                        style={{
                            fontSize: '12px',
                            display: 'block',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            marginBottom: 0,
                            color: themeColors.secondaryText
                        }}
                        title={`${t('category')}: ${agent.platform_account.category}`}
                    >
                        {t('category')}: {agent.platform_account.category}
                    </Text>
                )}
            </div>
        </Card>
    );
};

export default AgentCard;
